import { validateAutoVJSetBeforeSend, type AutoVJSet } from './autoVJ';
import { plainError, type PlainError } from './plainErrors';

/**
 * Local-first editing of ONE Auto VJ set document.
 *
 * The editor renders the draft this machine holds, and nothing the network does is allowed to block or
 * rewrite it. Every edit lands in the draft synchronously; a debounce later, the whole set goes out as
 * one `set_effect_document`, in the background, one call at a time — an edit made while a save is in
 * flight is folded into the next save instead of waiting for, or racing, the one on the wire.
 *
 * "Saved" is derived from the pushed room state, never from a command result (CONTRACT-console S17):
 * a save is confirmed when the listener delivers a set document equal to the last one this machine sent.
 * A delivery equal to an EARLIER send is this console's own echo arriving late and changes nothing. Any
 * other delivery is a change from another client: it never touches the draft while the draft carries
 * edits — the editor surfaces it as a notice and the operator decides whether to reload — and it is
 * adopted silently only while the draft is still exactly what was loaded.
 *
 * The machine is framework-free so the save/confirm/refuse rules are unit-testable without a DOM; the
 * screen mirrors the draft into a fine-grained store and re-reads `status()` on `onChange`.
 */

export type AutoVJDraftStatus =
  /** Loaded and untouched, or nothing this session has sent is outstanding. */
  | { kind: 'idle' }
  /** An edit is waiting out the debounce. */
  | { kind: 'pending' }
  /** A save is on the wire, or sent and not yet seen in the pushed room state. */
  | { kind: 'saving' }
  /** The pushed set equals the draft, and this session saved it. */
  | { kind: 'saved' }
  /** The draft cannot be sent as it stands; nothing was sent. */
  | { kind: 'invalid'; problem: PlainError }
  /** The daemon refused the save, or the room never published it. The draft is kept. */
  | { kind: 'refused'; problem: PlainError };

export interface AutoVJDraftDeps {
  /** Send the whole set document. Resolves when the command is accepted; rejects with the refusal. */
  send: (set: AutoVJSet) => Promise<unknown>;
  onChange: () => void;
  setTimer?: (run: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  debounceMs?: number;
  /** How long a sent document may go unpublished before the save is reported as unanswered. */
  unreportedMs: number;
}

export const AUTO_VJ_SAVE_DEBOUNCE_MS = 500;

/**
 * An order-free, emptiness-free identity for a set.
 *
 * The daemon round-trips the document through its own schema, so a pushed set may spell an empty list
 * as `null` where the draft holds `[]`, omit a baseline layer's absent driver, or order keys its own
 * way. None of that is a change anyone made, so none of it may read as a remote edit or hold "Saved"
 * back: absent, `null`, `[]` and `{}` are the same value here, and keys are compared sorted.
 */
export function autoVJSetKey(set: AutoVJSet): string {
  return JSON.stringify(canonical(set)) ?? '';
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.length ? value.map(canonical) : undefined;
  if (value !== null && typeof value === 'object') {
    const entries = Object.keys(value as Record<string, unknown>).sort().flatMap((key) => {
      const next = canonical((value as Record<string, unknown>)[key]);
      return next === undefined ? [] : [[key, next] as const];
    });
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  return value === null ? undefined : value;
}

/**
 * The console's default name for a new item in an ordered list: `Unit 1`, `Unit 2`, … — one past the
 * list's length, stepping on past any name already taken so two items never start out identical.
 */
export function nextDefaultName(prefix: string, taken: readonly string[]): string {
  const names = new Set(taken.map((name) => name.trim()));
  let index = taken.length + 1;
  while (names.has(`${prefix} ${index}`)) index += 1;
  return `${prefix} ${index}`;
}

/** A blank name is the one invalid draft an operator produces by typing, so it gets their words. */
function draftProblem(validation: string): PlainError {
  return /\.name must not be empty$/.test(validation)
    ? { message: 'Enter a name.', detail: validation }
    : plainError(validation);
}

const UNANSWERED = 'auto vj: the room did not publish the saved set before the command timed out';

export class AutoVJSetDraft {
  readonly id: string;
  private draft: AutoVJSet;
  private draftKey: string;
  /** Edited since load or reload: the draft is the operator's, not the room's. */
  private edited = false;
  private debounce: unknown;
  private unreported: unknown;
  private inFlight = false;
  /** An edit arrived while a save was on the wire; send again when it lands. */
  private queued = false;
  private lastSentKey?: string;
  private readonly sentKeys = new Set<string>();
  private confirmedKey?: string;
  /** Whether the room has ever published this set to this machine. */
  private published = false;
  private remote = false;
  private problem?: { kind: 'invalid' | 'refused'; problem: PlainError };
  private disposed = false;
  private sending?: Promise<void>;

  constructor(initial: AutoVJSet, private readonly deps: AutoVJDraftDeps, options: { published: boolean }) {
    this.id = initial.id;
    this.draft = structuredClone(initial);
    this.draftKey = autoVJSetKey(this.draft);
    this.published = options.published;
    if (options.published) this.confirmedKey = this.draftKey;
  }

  /** The draft as the editor shows it. A copy: the machine's value only moves through `edit`. */
  value(): AutoVJSet {
    return structuredClone(this.draft);
  }

  name(): string {
    return this.draft.name;
  }

  status(): AutoVJDraftStatus {
    if (this.problem) return this.problem.kind === 'invalid'
      ? { kind: 'invalid', problem: this.problem.problem }
      : { kind: 'refused', problem: this.problem.problem };
    if (this.debounce !== undefined) return { kind: 'pending' };
    if (this.inFlight || this.queued) return { kind: 'saving' };
    if (this.lastSentKey !== undefined && this.lastSentKey !== this.confirmedKey) return { kind: 'saving' };
    if (this.lastSentKey !== undefined && this.draftKey === this.confirmedKey) return { kind: 'saved' };
    return { kind: 'idle' };
  }

  /** Another client changed this set after this draft diverged from it. */
  remoteChanged(): boolean {
    return this.remote;
  }

  /** The draft still is exactly what was loaded, with nothing outstanding. */
  pristine(): boolean {
    return !this.edited && !this.inFlight && this.debounce === undefined;
  }

  edit(next: AutoVJSet): void {
    if (this.disposed) return;
    this.draft = structuredClone(next);
    this.draftKey = autoVJSetKey(this.draft);
    this.edited = true;
    this.problem = undefined;
    this.clear('debounce');
    this.debounce = this.timer(() => {
      this.debounce = undefined;
      this.send();
    }, this.deps.debounceMs ?? AUTO_VJ_SAVE_DEBOUNCE_MS);
    this.deps.onChange();
  }

  /**
   * Send now whatever the debounce is holding. Resolves once the save on the wire (if any) has been
   * answered, accepted or refused — never rejects; the outcome is in `status()`.
   */
  flush(): Promise<void> {
    if (this.debounce !== undefined) {
      this.clear('debounce');
      this.send();
    }
    return this.inFlight && this.sending ? this.sending : Promise.resolve();
  }

  /**
   * A delivery from the room's listener: the set's current document, or `undefined` once it is gone.
   * Returns `adopt` when the screen should replace its draft with the room's (the draft is pristine).
   */
  observe(pushed: AutoVJSet | undefined): 'adopt' | 'none' {
    if (this.disposed) return 'none';
    if (!pushed) {
      if (!this.published) return 'none';
      if (this.pristine()) return 'adopt';
      if (!this.remote) {
        this.remote = true;
        this.deps.onChange();
      }
      return 'none';
    }
    this.published = true;
    const key = autoVJSetKey(pushed);
    if (key === this.lastSentKey) {
      this.confirmedKey = key;
      this.clear('unreported');
      if (this.problem?.problem.detail === UNANSWERED) this.problem = undefined;
      this.deps.onChange();
      return 'none';
    }
    if (this.sentKeys.has(key)) return 'none';
    if (key === this.draftKey) {
      this.confirmedKey = key;
      this.deps.onChange();
      return 'none';
    }
    if (this.pristine()) return 'adopt';
    if (!this.remote) {
      this.remote = true;
      this.deps.onChange();
    }
    return 'none';
  }

  /** Replace the draft with the room's set — a reload, or adopting a change while pristine. */
  reset(pushed: AutoVJSet): void {
    this.clear('debounce');
    this.clear('unreported');
    this.draft = structuredClone(pushed);
    this.draftKey = autoVJSetKey(this.draft);
    this.confirmedKey = this.draftKey;
    this.lastSentKey = undefined;
    this.sentKeys.clear();
    this.queued = false;
    this.edited = false;
    this.remote = false;
    this.problem = undefined;
    this.published = true;
    this.deps.onChange();
  }

  /** Stop saving this set: it was deleted, or the editor is gone and has flushed it. */
  dispose(): void {
    this.disposed = true;
    this.clear('debounce');
    this.clear('unreported');
  }

  private send(): void {
    if (this.disposed) return;
    if (this.inFlight) {
      this.queued = true;
      this.deps.onChange();
      return;
    }
    // Already on its way, or already what the room holds. Compared with the LAST thing sent rather
    // than with the room: an edit back to the room's value while a different value is on the wire
    // must still go out, or the room ends on the value in flight.
    if (this.draftKey === (this.lastSentKey ?? this.confirmedKey)) {
      this.deps.onChange();
      return;
    }
    const validation = validateAutoVJSetBeforeSend(this.draft);
    if (validation) {
      this.problem = { kind: 'invalid', problem: draftProblem(validation) };
      this.deps.onChange();
      return;
    }
    const sent = structuredClone(this.draft);
    const key = this.draftKey;
    this.inFlight = true;
    this.lastSentKey = key;
    this.sentKeys.add(key);
    this.clear('unreported');
    this.deps.onChange();
    this.sending = this.deps.send(sent).then(() => {
      this.inFlight = false;
      if (this.disposed) return;
      this.problem = undefined;
      if (this.confirmedKey !== key && this.lastSentKey === key) {
        this.unreported = this.timer(() => {
          this.unreported = undefined;
          if (this.lastSentKey !== this.confirmedKey && !this.problem) {
            this.problem = { kind: 'refused', problem: plainError(UNANSWERED) };
            this.deps.onChange();
          }
        }, this.deps.unreportedMs);
      }
      this.afterSend();
    }, (error: unknown) => {
      this.inFlight = false;
      if (this.disposed) return;
      // The refused document was never stored, so it may not count as sent: a later edit back to the
      // same value must go out again, and "Saved" must not wait for an echo that will never come.
      this.lastSentKey = this.confirmedKey;
      this.problem = { kind: 'refused', problem: plainError(error) };
      this.afterSend();
    });
  }

  private afterSend(): void {
    if (this.queued) {
      this.queued = false;
      if (this.debounce === undefined) this.send();
    }
    this.deps.onChange();
  }

  private timer(run: () => void, ms: number): unknown {
    return (this.deps.setTimer ?? globalThis.setTimeout)(run, ms);
  }

  private clear(which: 'debounce' | 'unreported'): void {
    const handle = this[which];
    if (handle === undefined) return;
    (this.deps.clearTimer ?? ((value: unknown) => globalThis.clearTimeout(value as ReturnType<typeof setTimeout>)))(handle);
    this[which] = undefined;
  }
}
